import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useI18n, catName } from "@/lib/i18n";
import ProductCard from "@/components/ProductCard";
import SmartSearchBar from "@/components/SmartSearchBar";
import { Sparkles, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export default function Explore() {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const [sort, setSort] = useState("recent");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiRanked, setAiRanked] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setAiRanked(false);
    try {
      if (q) {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setProducts(data.results);
        setAiRanked(data.ai_ranked);
      } else {
        const qs = new URLSearchParams({ sort });
        if (category) qs.set("category", category);
        if (verifiedOnly) qs.set("verified_only", "true");
        const { data } = await api.get(`/products?${qs.toString()}`);
        setProducts(data);
      }
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [q, category, sort, verifiedOnly]);

  useEffect(() => { load(); }, [load]);

  const setCategory = (val) => {
    const next = new URLSearchParams(params);
    next.delete("q");
    if (val === "all") next.delete("category"); else next.set("category", val);
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="max-w-2xl mb-8"><SmartSearchBar /></div>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0F172A]">
            {q ? `"${q}"` : t("explore.title")}
          </h1>
          <p className="text-[#475569] mt-1 flex items-center gap-2">
            {products.length} {t("explore.results")}
            {q && aiRanked && (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0EA5E9] bg-sky-50 rounded-full px-2.5 py-1">
                <Sparkles className="h-3 w-3" /> AI
              </span>
            )}
          </p>
        </div>
        {!q && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-full px-4 py-2 cursor-pointer" data-testid="verified-toggle-wrap">
              <Switch checked={verifiedOnly} onCheckedChange={setVerifiedOnly} data-testid="verified-toggle" />
              <span className="text-sm font-semibold text-[#475569]">{t("explore.verifiedOnly")}</span>
            </label>
            <Select value={category || "all"} onValueChange={setCategory}>
              <SelectTrigger className="w-[190px] rounded-full bg-white" data-testid="filter-category">
                <SelectValue placeholder={t("explore.allCategories")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("explore.allCategories")}</SelectItem>
                {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{catName(c, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[190px] rounded-full bg-white" data-testid="filter-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">{t("explore.sort.recent")}</SelectItem>
                <SelectItem value="popular">{t("explore.sort.popular")}</SelectItem>
                <SelectItem value="rating">{t("explore.sort.rating")}</SelectItem>
                <SelectItem value="price_low">{t("explore.sort.price_low")}</SelectItem>
                <SelectItem value="price_high">{t("explore.sort.price_high")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32 text-[#94A3B8]">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> {t("common.loading")}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-32 text-[#94A3B8]" data-testid="explore-empty">{t("explore.empty")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="explore-grid">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
